// src/modules/Shop/pages/Home.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowRight, FaRobot, FaMicrochip, FaTools, FaGraduationCap } from 'react-icons/fa';
import { supabase } from '../../../config/supabase';
import ProductGrid from '../components/ProductGrid';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #111 0%, #1a1a1a 100%);
  color: white;
  padding: 80px 24px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 48px 16px;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 16px;
  line-height: 1.2;

  span {
    color: #00D4AA;
  }

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 32px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const HeroButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #00D4AA;
  color: #000;
  padding: 16px 32px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  transition: all 200ms;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 212, 170, 0.3);
  }

  svg {
    transition: transform 200ms;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;

const Categories = styled.section`
  padding: 64px 24px;
  background: #f8f8f8;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #111;
`;

const ViewAllLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 200ms;

  &:hover {
    color: #00D4AA;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled(Link)`
  background: white;
  border-radius: 16px;
  padding: 32px;
  text-decoration: none;
  text-align: center;
  transition: all 300ms;
  border: 1px solid #eee;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
  }
`;

const CategoryIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f0f0f0;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  transition: all 300ms;

  svg {
    font-size: 28px;
    color: #666;
  }

  ${CategoryCard}:hover & {
    background: #00D4AA;

    svg {
      color: #000;
    }
  }
`;

const CategoryName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  margin-bottom: 4px;
`;

const CategoryCount = styled.span`
  font-size: 13px;
  color: #999;
`;

const ProductsSection = styled.section`
  padding: 64px 24px;
`;

const FeaturesSection = styled.section`
  padding: 64px 24px;
  background: #111;
  color: white;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  text-align: center;
`;

const FeatureIcon = styled.div`
  font-size: 32px;
  margin-bottom: 16px;
`;

const FeatureTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const FeatureText = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
`;

const categoryIcons = {
  'robos-colaborativos': FaRobot,
  'sensores': FaMicrochip,
  'acessorios': FaTools,
  'kits-educacionais': FaGraduationCap
};

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Buscar categorias
      const { data: categoriesData } = await supabase
        .from('shop_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      // Buscar produtos em destaque
      const { data: featuredData } = await supabase
        .from('shop_products')
        .select('*, category:shop_categories(name, slug)')
        .eq('is_active', true)
        .eq('is_featured', true)
        .limit(8);

      // Buscar produtos novos
      const { data: newData } = await supabase
        .from('shop_products')
        .select('*, category:shop_categories(name, slug)')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(8);

      setCategories(categoriesData || []);
      setFeaturedProducts(featuredData || []);
      setNewProducts(newData || []);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>
            Tecnologia que <span>transforma</span> sua industria
          </HeroTitle>
          <HeroSubtitle>
            Robos colaborativos, sensores e solucoes completas para automacao industrial.
            Entrega em todo Brasil com suporte tecnico especializado.
          </HeroSubtitle>
          <HeroButton to="/categoria/robos-colaborativos">
            Ver Produtos <FaArrowRight />
          </HeroButton>
        </HeroContent>
      </Hero>

      <Categories>
        <Container>
          <SectionHeader>
            <SectionTitle>Categorias</SectionTitle>
          </SectionHeader>

          <CategoriesGrid>
            {categories.map(category => {
              const IconComponent = categoryIcons[category.slug] || FaRobot;
              return (
                <CategoryCard key={category.id} to={`/categoria/${category.slug}`}>
                  <CategoryIcon>
                    <IconComponent />
                  </CategoryIcon>
                  <CategoryName>{category.name}</CategoryName>
                  <CategoryCount>{category.description}</CategoryCount>
                </CategoryCard>
              );
            })}
          </CategoriesGrid>
        </Container>
      </Categories>

      <ProductsSection>
        <Container>
          <SectionHeader>
            <SectionTitle>Produtos em Destaque</SectionTitle>
            <ViewAllLink to="/categoria/todos">
              Ver todos <FaArrowRight />
            </ViewAllLink>
          </SectionHeader>

          <ProductGrid products={featuredProducts} loading={loading} />
        </Container>
      </ProductsSection>

      <FeaturesSection>
        <FeaturesGrid>
          <FeatureItem>
            <FeatureIcon>🚚</FeatureIcon>
            <FeatureTitle>Frete Gratis</FeatureTitle>
            <FeatureText>Para compras acima de R$ 500 em todo Brasil</FeatureText>
          </FeatureItem>

          <FeatureItem>
            <FeatureIcon>💳</FeatureIcon>
            <FeatureTitle>Parcele em 12x</FeatureTitle>
            <FeatureText>Sem juros no cartao de credito</FeatureText>
          </FeatureItem>

          <FeatureItem>
            <FeatureIcon>🔒</FeatureIcon>
            <FeatureTitle>Compra Segura</FeatureTitle>
            <FeatureText>Pagamento 100% seguro via Stripe</FeatureText>
          </FeatureItem>

          <FeatureItem>
            <FeatureIcon>🛠️</FeatureIcon>
            <FeatureTitle>Suporte Tecnico</FeatureTitle>
            <FeatureText>Equipe especializada para te ajudar</FeatureText>
          </FeatureItem>
        </FeaturesGrid>
      </FeaturesSection>

      <ProductsSection>
        <Container>
          <SectionHeader>
            <SectionTitle>Novidades</SectionTitle>
            <ViewAllLink to="/categoria/todos?sort=newest">
              Ver todos <FaArrowRight />
            </ViewAllLink>
          </SectionHeader>

          <ProductGrid products={newProducts} loading={loading} />
        </Container>
      </ProductsSection>
    </PageContainer>
  );
};

export default Home;
